import { Container, Stack, Toolbar } from "@mui/material";
import useTheme from "@mui/material/styles/useTheme";
import { LucideSearch } from "lucide-react";
import { type Dispatch, type SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { ChatbotDrawer } from "./ChatbotDrawer.component";
import { CloseDropdowns } from "./CloseDropdowns.component";
import { HeaderActionsBar } from "./HeaderActionsBar.component";
import { LanguageSelector } from "./LanguageSelector.component";
import { LinkLogo } from "./LinkLogo.component";
import { MobileSearchDrawer } from "./MobileSearchDrawer.component";
import { NotificationsDrawer } from "./NotificationsDrawer.component";
import { SupportAiButton } from "./SupportAiButton.component";
import { Button, LanguageModal } from "../../../components";
import { ImpersonationModal } from "../../../components/ImpersonationModal";
import type { Language } from "../../../types";
import { DROPDOWN, type Dropdown } from "../Header.consts";
import { HeaderAppBar } from "../Header.styles";

type HeaderMobileProps = {
  authenticated: boolean
  showTutorialUserMenu?: boolean
  isMultilanguage?: boolean
  languages?: Language[]
  isPreLogin?: boolean
  dropdown: Dropdown | null
  setDropdown: Dispatch<SetStateAction<Dropdown | null>>
  anchorElDropdown: HTMLElement | null
  setAnchorElDropdown: Dispatch<SetStateAction<HTMLElement | null>>
  mobileSearchOpen: boolean
  setMobileSearchOpen: Dispatch<SetStateAction<boolean>>
  searchValue: string
  setSearchValue: Dispatch<SetStateAction<string>>
  languageModalOpen: boolean
  setLanguageModalOpen: Dispatch<SetStateAction<boolean>>
  impersonationModalOpen: boolean
  setImpersonationModalOpen: Dispatch<SetStateAction<boolean>>
}

export function HeaderMobile({
  authenticated,
  showTutorialUserMenu,
  isMultilanguage,
  languages,
  isPreLogin,
  dropdown,
  setDropdown,
  anchorElDropdown,
  setAnchorElDropdown,
  mobileSearchOpen,
  setMobileSearchOpen,
  searchValue,
  setSearchValue,
  languageModalOpen,
  setLanguageModalOpen,
  impersonationModalOpen,
  setImpersonationModalOpen
}: HeaderMobileProps) {
  const theme = useTheme();
  const { t } = useTranslation();

  return (
    <HeaderAppBar id="app-bar" $mobileSearchOpen={ mobileSearchOpen } role="none">
      <CloseDropdowns closeDropdowns={ () => { setDropdown(null) } }/>
      <ChatbotDrawer
        hideChatbot={ () => setDropdown(null) }
        showChatBot={ dropdown === DROPDOWN.CHATBOT }
      />
      {
        mobileSearchOpen ?
          <MobileSearchDrawer
            mobileSearchOpen={ mobileSearchOpen }
            searchValue={ searchValue }
            setMobileSearchOpen={ setMobileSearchOpen }
            setSearchValue={ setSearchValue }
          /> : null
      }

      {
        authenticated ?
          <NotificationsDrawer
            hideNotification={ () => setDropdown(null) }
            showNotifications={ dropdown === DROPDOWN.NOTIFICATIONS }
          /> : null
      }

      <Container maxWidth="max" sx={ { "& .MuiToolbar-root": { paddingLeft: "0" } } }>
        <Toolbar sx={ { minHeight: "auto", p: "0" } }>
          { /* left side elements */ }
          <LinkLogo authenticated={ authenticated }/>
          { /* right side elements */ }
          {
            authenticated ? (
              <HeaderActionsBar
                variant="mobile"
                isMultilanguage={ Boolean(isMultilanguage) }
                showTutorialUserMenu={ showTutorialUserMenu }
                dropdown={ dropdown }
                setDropdown={ setDropdown }
                anchorElDropdown={ anchorElDropdown }
                setAnchorElDropdown={ setAnchorElDropdown }
                setLanguageModalOpen={ setLanguageModalOpen }
                setImpersonationModalOpen={ setImpersonationModalOpen }
                leadingElement={
                  <Button
                    aria-label={ t("search_contents") }
                    className={ "search-opener" }
                    disableRipple
                    onClick={ () => { setMobileSearchOpen(true) } }
                    sx={ {
                      "&:focus-visible": {
                        background: "transparent",
                        boxShadow: "none"
                      },
                      height: "100%",
                      minWidth:"unset",
                      padding: "0",
                      zIndex: theme.zIndex.fab
                    } }
                  >
                    <LucideSearch
                      size={ 24 }
                      color={ theme.customColors.textPrimary }
                    />
                  </Button>
                }
              />
            ) : null
          }
          {
            !authenticated && (isPreLogin || isMultilanguage) ? (
              <Stack
                alignItems="center"
                direction="row"
                flexGrow={ 1 }
                gap={ 2 }
                justifyContent="flex-end"
                zIndex={ theme.zIndex.fab }
              >
                { isPreLogin ? <SupportAiButton /> : null }
                { isMultilanguage ? (
                  <LanguageSelector
                    setOpenLanguageModal={ setLanguageModalOpen } />
                ) : null }
              </Stack>
            ) : null
          }
        </Toolbar>
      </Container>
      <LanguageModal
        close={ () => setLanguageModalOpen(false) }
        languages={ languages ?? [] }
        open={ languageModalOpen }
      />
      <ImpersonationModal
        close={ () => setImpersonationModalOpen(false) }
        open={ impersonationModalOpen }
      />
    </HeaderAppBar>
  );
}
